import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Shield, Share, PlusSquare } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function InstallPWAAdmin() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isSafari, setIsSafari] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed as standalone
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
      setIsStandalone(true);
      return;
    }

    // Check if iOS device
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);
    
    // Check if Safari on iOS
    const isSafariBrowser = /Safari/.test(navigator.userAgent) && 
                            !/CriOS|FxiOS|OPiOS|EdgiOS/.test(navigator.userAgent);
    setIsSafari(isSafariBrowser);

    // Helper to check if should show banner
    const shouldShowBanner = () => {
      const dismissed = localStorage.getItem('pwa-admin-dismissed');
      if (!dismissed) return true;
      
      // Check if it's a timestamp (ISO date) or old 'true' string
      const dismissedTime = new Date(dismissed).getTime();
      if (isNaN(dismissedTime)) {
        // Old format 'true' - clear and show
        localStorage.removeItem('pwa-admin-dismissed');
        return true;
      }
      
      const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
      return dismissedTime < oneDayAgo;
    };

    // Listen for beforeinstallprompt (Android/Desktop)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show banner after short delay
      setTimeout(() => {
        if (shouldShowBanner()) {
          setShowInstallBanner(true);
        }
      }, 2000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show banner for all users after delay (for iOS or if beforeinstallprompt didn't fire)
    const timer = setTimeout(() => {
      if (shouldShowBanner()) {
        setShowInstallBanner(true);
      }
    }, 3000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      clearTimeout(timer);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Use native prompt if available
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setShowInstallBanner(false);
        localStorage.setItem('pwa-admin-dismissed', new Date().toISOString());
      }
    } else {
      // Show manual instructions
      setShowInstructions(true);
    }
  };

  const handleDismiss = () => {
    setShowInstallBanner(false);
    setShowInstructions(false);
    localStorage.setItem('pwa-admin-dismissed', new Date().toISOString());
  };

  // Don't show if already installed
  if (isStandalone || !showInstallBanner) return null;

  return (
    <AnimatePresence>
      {showInstallBanner && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-sm"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div className="bg-slate-800 rounded-2xl shadow-2xl border border-slate-700 overflow-hidden">
            {showInstructions ? (
              // Manual Installation Instructions
              <div className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-600 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">Install Admin Panel</h3>
                      <p className="text-xs text-slate-400">Aplikasi terpisah dari user</p>
                    </div>
                  </div>
                  <button onClick={handleDismiss} className="text-slate-400 hover:text-slate-200">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                {isIOS ? (
                  // iOS Instructions - Enhanced
                  <>
                    {!isSafari && (
                      <div className="bg-amber-900/50 border border-amber-700 rounded-lg p-3 mb-3">
                        <p className="text-amber-200 text-sm">
                          <strong>Catatan:</strong> Install PWA di iPhone hanya bisa melalui <strong>Safari</strong>.
                        </p>
                      </div>
                    )}
                    <div className="space-y-3 text-sm">
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 bg-orange-900 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-orange-300 font-bold text-xs">1</span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 text-slate-200">
                            <span>Tekan tombol</span>
                            <Share className="w-4 h-4 text-orange-400" />
                            <span className="font-medium">Share</span>
                          </div>
                          <p className="text-xs text-slate-400">Di bagian bawah Safari</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 bg-orange-900 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-orange-300 font-bold text-xs">2</span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 text-slate-200">
                            <span>Pilih</span>
                            <PlusSquare className="w-4 h-4 text-orange-400" />
                            <span className="font-medium">&quot;Add to Home Screen&quot;</span>
                          </div>
                          <p className="text-xs text-slate-400">Scroll ke bawah jika tidak terlihat</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 bg-orange-900 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-orange-300 font-bold text-xs">3</span>
                        </div>
                        <div className="flex-1 text-slate-200">
                          Tekan <span className="font-medium text-orange-400">&quot;Add&quot;</span> di pojok kanan atas
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  // Android/Desktop Instructions
                  <ol className="text-sm text-slate-300 space-y-2 ml-2">
                    <li>1. Tap <strong className="text-white">⋮</strong> (titik tiga) di pojok kanan atas Chrome</li>
                    <li>2. Pilih <strong className="text-white">&quot;Install app&quot;</strong> atau <strong className="text-white">&quot;Add to Home screen&quot;</strong></li>
                    <li>3. Tap <strong className="text-white">&quot;Install&quot;</strong></li>
                  </ol>
                )}
                <Button 
                  onClick={handleDismiss}
                  size="sm"
                  className="w-full mt-4 bg-slate-700 hover:bg-slate-600 rounded-full"
                >
                  Mengerti
                </Button>
              </div>
            ) : (
              // Install Banner
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white mb-1">Install Admin Panel</h3>
                    <p className="text-sm text-slate-400 mb-3">
                      Aplikasi admin terpisah di home screen HP
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        onClick={handleInstall}
                        size="sm"
                        className="bg-orange-600 hover:bg-orange-700 rounded-full"
                        data-testid="install-admin-pwa-btn"
                      >
                        Install
                      </Button>
                      <Button 
                        onClick={handleDismiss}
                        variant="ghost"
                        size="sm"
                        className="rounded-full text-slate-300 hover:text-white hover:bg-slate-700"
                      >
                        Nanti
                      </Button>
                    </div>
                  </div>
                  <button onClick={handleDismiss} className="text-slate-400 hover:text-slate-200">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
